import React from 'react';
import {
  createStyles,
  makeStyles,
  Grid,
  ImageListItem,
  Typography,
  CircularProgress,
  Button,
  Theme,
  ButtonBase,
} from '@material-ui/core';
import { colors, svgs } from 'design-system';

import FileUploadState from '../../../../models/helperTypes/FileUploadState';

type LocalTheme = {
  image: string;
  state: FileUploadState;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: `${theme.spacing(25)} !important`,
      position: 'relative',
    },
    tileImage: ({ image, state }: LocalTheme) => ({
      position: 'relative',
      backgroundSize: 'cover',
      width: 200,
      height: 150,
      overflow: 'hidden',
      backgroundPosition: 'center',
      border: `1px solid ${colors.silver}`,
      borderRadius: 10,
      backgroundImage: `url(${image})`,
      // overlay is active when state is either "uploading" or "failed"
      '&::after': {
        position: 'absolute',
        content: '""',
        top: 0,
        left: 0,
        zIndex: 1,
        width: 200,
        height: 150,
        borderRadius: 10,
        backgroundColor: 'rgba(85, 86, 89)',
        opacity: ['failed', 'uploading'].includes(state) ? 0.85 : 0,
      },
    }),
    editPhotoIcon: {
      position: 'absolute',
      top: theme.spacing(2.25),
      right: theme.spacing(2.25),
    },
    checked: {
      position: 'absolute',
      top: theme.spacing(2.25),
      left: theme.spacing(3.125),
      height: theme.spacing(2.5),
      width: theme.spacing(3.4),
    },
    statePosition: {
      position: 'absolute',
      top: '45%',
      left: 0,
      transform: 'translateY(-50%)',
      zIndex: 100,
    },
    actionButton: {
      backgroundColor: colors.white,
      color: colors.assa_blue,
      borderRadius: theme.spacing(2.5),
      width: theme.spacing(16.5),
      height: theme.spacing(4),
    },
    spinner: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      marginBottom: theme.spacing(1.5),
    },
  }),
);

type UseStylesType = ReturnType<typeof useStyles>;

type PictureProps = {
  image: string;
  state: FileUploadState;
  section: string;
  onRetakeAction: () => void;
  onRetryAction: () => void;
  onCancelAction: () => void;
};

function renderRetry(
  classes: UseStylesType,
  onRetryAction: () => void,
): JSX.Element {
  const { spinArrow } = svgs;

  return (
    <Grid container className={classes.statePosition} data-testid="retry">
      <Grid item xs={12} container justifyContent="center">
        <img src={spinArrow} alt="retry-icon" />
      </Grid>
      <Grid item xs={12} container justifyContent="center">
        <Button
          data-testid="retry-action"
          variant="contained"
          className={classes.actionButton}
          onClick={onRetryAction}
        >
          REINTENTAR
        </Button>
      </Grid>
    </Grid>
  );
}

function renderCancel(
  classes: UseStylesType,
  onCancelAction: () => void,
): JSX.Element {
  return (
    <Grid container className={classes.statePosition} data-testid="spinner">
      <Grid item xs={12} container justifyContent="center">
        <CircularProgress color="inherit" className={classes.spinner} />
      </Grid>
      <Grid item xs={12} container justifyContent="center">
        <Button
          data-testid="cancel-action"
          variant="contained"
          className={classes.actionButton}
          onClick={onCancelAction}
        >
          CANCELAR
        </Button>
      </Grid>
    </Grid>
  );
}

function renderPictureState(
  state: FileUploadState,
  classes: UseStylesType,
  onRetryAction: () => void,
  onCancelAction: () => void,
): JSX.Element {
  const { check } = svgs;
  switch (state) {
    case 'success': {
      return (
        <img
          src={check}
          alt="check-icon"
          className={classes.checked}
          data-testid="check-icon"
        />
      );
    }
    case 'failed': {
      return renderRetry(classes, onRetryAction);
    }
    case 'uploading': {
      return renderCancel(classes, onCancelAction);
    }
    default: {
      return <div />;
    }
  }
}

const Picture = ({
  image,
  state,
  section,
  onRetakeAction,
  onRetryAction,
  onCancelAction,
}: PictureProps): JSX.Element => {
  const classes = useStyles({ image, state });
  const { magnifier } = svgs;

  return (
    <ImageListItem data-testid="image" className={classes.root}>
      <ButtonBase
        data-testid="retake-action"
        onClick={onRetakeAction}
        disabled={['failed', 'uploading'].includes(state)}
      >
        <div className={classes.tileImage} />
      </ButtonBase>
      <Grid container justifyContent="center">
        <Typography variant="caption" data-testid="section">
          {section}
        </Typography>
      </Grid>
      <div className={classes.editPhotoIcon}>
        <img src={magnifier} alt="zoom-icon" width="27" />
      </div>
      {renderPictureState(state, classes, onRetryAction, onCancelAction)}
    </ImageListItem>
  );
};

export default Picture;
